import useAnalyticsContext from '|/hooks/use-analytics-context';
import { datadogRum } from '@datadog/browser-rum';
import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import UserInitializationContext from '|/context/user-initialization-context';
import useDocumentTitle from '|/hooks/use-document-title';
import useLoad from '|/hooks/use-load';
import useNavigateToNextStep from '|/hooks/use-navigate-to-next-step';
import useSubmit from '|/hooks/use-submit';
import {
  getGapWaiver,
  GapWaiverResponse,
  addGapWaiver,
  declineGapWaiver,
  resetGapWaiver
} from './services';

const isAbortError = (error: unknown): boolean => {
  return error instanceof Error && (error.name === 'CanceledError' || error.name === 'AbortError');
};

const useGapCoverageVm = () => {
  useDocumentTitle('Gap coverage');
  const { pathname } = useLocation();
  const { trackEvent } = useAnalyticsContext();
  const { accessToken } = useContext(UserInitializationContext);
  const navigateToNextStep = useNavigateToNextStep();
  const load = useLoad();
  const submit = useSubmit();

  const [gapWaiver, setGapWaiver] = useState<GapWaiverResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnavailableModalOpen, setIsUnavailableModalOpen] = useState(false);
  const submitAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      submitAbortRef.current?.abort();
    };
  }, []);

  const sendEventData = useCallback(
    (eventName: string, status: string, gapWaiver: GapWaiverResponse | null) => {
      trackEvent(eventName, {
        url: pathname,
        userId: accessToken?.user.user_id,
        vehicleId: gapWaiver?.vehicleId,
        successEvent: status,
        metadata: {
          isGapApplied: gapWaiver ? (gapWaiver.isApplied ? 'Added' : 'Declined') : 'Unavailable',
          ...(gapWaiver && {
            gapPrice: gapWaiver.price,
            annualPercentageRate: gapWaiver.financing.annualPercentageRate,
            monthlyPayment: gapWaiver.financing.monthlyPayment,
            modifiedMonthlyPayment: gapWaiver.financing.modifiedMonthlyPayment,
            monthlyPaymentDifference: gapWaiver.financing.monthlyPaymentDifference,
            downPayment: gapWaiver.financing.downPayment,
            modifiedDownPayment: gapWaiver.financing.modifiedDownPayment,
            downPaymentDifference: gapWaiver.financing.downPaymentDifference,
            termLength: gapWaiver.financing.termLength,
            termLengthDifference: gapWaiver.financing.termLengthDifference
          })
        },
        devicePlatform: 'web'
      });
    },
    [pathname, trackEvent, accessToken]
  );

  useEffect(() => {
    const abortController = new AbortController();

    const loadGapWaiver = async (): Promise<void> => {
      try {
        const data = await getGapWaiver(abortController.signal);
        setGapWaiver(data);

        if (data.isAvailable) {
          sendEventData('pp_load', 'True Complete', data);
        } else {
          // GAP is unavailable - decline and reset before showing modal
          try {
            await declineGapWaiver(abortController.signal);
            await resetGapWaiver(abortController.signal);
          } catch (apiError) {
            // Continue to show modal even if decline/reset fails
            if (isAbortError(apiError)) {
              return;
            }
          }

          // Only open modal after decline/reset completes (or fails)
          setIsUnavailableModalOpen(true);
        }
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }
        setLoadError("We're sorry, an issue has occurred. Please try again later.");
        throw error;
      }
    };

    load(loadGapWaiver(), abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [load, sendEventData, setLoadError]);

  const handleContinue = useCallback(() => {
    setIsSubmitting(true);
    const abortController = new AbortController();
    submitAbortRef.current = abortController;

    const submitContinue = async (): Promise<void> => {
      try {
        if (!gapWaiver?.isApplied) {
          await addGapWaiver(abortController.signal);
          datadogRum.setGlobalContextProperty('gapWaiverAttached', true);
        }

        sendEventData(
          'pp_complete',
          'True Complete',
          gapWaiver && { ...gapWaiver, isApplied: true }
        );
        trackEvent('Gap Waiver Selected');
        navigateToNextStep();
      } catch (error) {
        setIsSubmitting(false);
        setLoadError('There was an issue adding gap waiver coverage');
        sendEventData('pp_complete', 'False', gapWaiver);

        if (isAbortError(error)) {
          return;
        }
      }
    };

    submit(() => submitContinue(), abortController.signal);
  }, [submit, sendEventData, trackEvent, navigateToNextStep, gapWaiver]);

  const handleDecline = useCallback(() => {
    setIsSubmitting(true);
    const abortController = new AbortController();
    submitAbortRef.current = abortController;

    const submitDecline = async (): Promise<void> => {
      try {
        await declineGapWaiver(abortController.signal);

        sendEventData(
          'pp_complete',
          'True Complete',
          gapWaiver && { ...gapWaiver, isApplied: false }
        );
        trackEvent('Gap Waiver Declined');
        datadogRum.setGlobalContextProperty('gapWaiverAttached', false);
        navigateToNextStep();
      } catch (error) {
        setIsSubmitting(false);
        setLoadError('There was an issue declining gap waiver coverage');
        sendEventData('pp_complete', 'False', gapWaiver);

        if (isAbortError(error)) {
          return;
        }
      }
    };

    submit(() => submitDecline(), abortController.signal);
  }, [submit, sendEventData, trackEvent, navigateToNextStep, gapWaiver]);

  const handleUnavailableModalClose = useCallback(() => {
    setIsUnavailableModalOpen(false);
    sendEventData('pp_complete', 'True Complete', null);
  }, [sendEventData]);

  return {
    gapWaiver,
    sendEventData,
    loadError,
    isSubmitting,
    handleContinue,
    handleDecline,
    isUnavailableModalOpen,
    handleUnavailableModalClose
  };
};

export default useGapCoverageVm;
